import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  type OnChanges,
  type OnInit,
  Output,
  type SimpleChanges,
} from '@angular/core';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { textVariants } from '@freelancer/ui/tailwind-text';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { ModalColor } from './modal-color';

@Component({
  standalone: false,
  selector: 'fl-modal-error',
  template: `
    <div
      class="ModalError"
      [attr.data-edge-to-edge]="edgeToEdge"
    >
      <h3
        class="text-mid"
        i18n="Modal load error title"
        [flMarginBottom]="'mid'"
        [class.text-foreground]="color === ModalColor.LIGHT"
        [class.text-light]="!(color === ModalColor.LIGHT)"
      >
        Aw, snap!
      </h3>

      <p
        class="{{
          color === ModalColor.LIGHT ? 'text-foreground' : 'text-light'
        }}"
        i18n="Modal load error description"
        [flMarginBottom]="'small'"
      >
        Something went wrong while fetching the content. Please check your
        Internet connectivity and then retry.
      </p>

      <div [flMarginBottom]="'mid'">
        <div
          [class]="errorTextClasses"
          [flMarginBottom]="'xxsmall'"
        >
          <fl-button (click)="toggleError()">
            @if (!isErrorDisplayed) {
              <ng-container i18n="Modal load error details show more link">
                Show more information
              </ng-container>
            }
            @if (isErrorDisplayed) {
              <ng-container i18n="Modal load error details hide more link">
                Hide more information
              </ng-container>
            }
            <fl-icon
              class="ModalError-icon"
              [class.IsActive]="isErrorDisplayed"
              [color]="
                color === ModalColor.LIGHT
                  ? IconColor.FOREGROUND
                  : IconColor.LIGHT
              "
              [name]="'ui-chevron-down'"
              [size]="IconSize.SMALL"
              (click)="toggleError()"
            ></fl-icon>
          </fl-button>
        </div>

        @if (isErrorDisplayed) {
          <p class="ModalError-details">
            {{ errorMessage }}
          </p>
        }
      </div>

      <div class="ModalError-actions">
        <fl-button
          class="ModalError-retryBtn"
          i18n="Modal load error retry button"
          [color]="ButtonColor.SECONDARY"
          [size]="ButtonSize.SMALL"
          (click)="handleRetryClick()"
        >
          Retry
        </fl-button>
      </div>
    </div>
  `,
  styleUrls: ['./modal-error.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalErrorComponent implements OnInit, OnChanges {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  IconColor = IconColor;
  IconSize = IconSize;
  ModalColor = ModalColor;

  @Input() color = ModalColor.LIGHT;
  @Input() edgeToEdge = false;
  @Input() errorMessage: string;

  @Output() retry = new EventEmitter<void>();

  isErrorDisplayed = false;

  errorTextClasses: string;

  ngOnInit(): void {
    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('color' in changes) {
      this.computeClasses();
    }
  }

  private computeClasses(): void {
    this.errorTextClasses = textVariants({
      color:
        this.color === ModalColor.LIGHT
          ? FontColor.FOREGROUND
          : FontColor.LIGHT,
      size: TextSize.XSMALL,
      weight: FontWeight.NORMAL,
    });
  }

  handleRetryClick(): void {
    this.retry.emit();
  }

  toggleError(): void {
    this.isErrorDisplayed = !this.isErrorDisplayed;
  }
}
